<template>
  <div id="app">
    <!-- 상단 네비게이션 바 -->
    <nav class="top-navbar">
      <div class="nav-wrapper">
        <router-link to="/" class="logo-link">
          <img :src="logoImage" alt="Pipeline" class="logo-image">
        </router-link>
        
        <!-- 로그인 상태에 따른 조건부 렌더링 -->
        <div v-if="currentUser" class="user-profile" @click="toggleDropdown">
          <span class="username">{{ currentUser.displayName || '사용자' }}</span>
          <i class="fas fa-chevron-down"></i>
          
          <!-- 드롭다운 메뉴 -->
          <div v-if="showDropdown" class="dropdown-menu">
            <router-link to="/setting" class="dropdown-item">
              <i class="fas fa-user-cog"></i>
              프로필 설정
            </router-link>
            <button @click="handleLogout" class="dropdown-item logout">
              <i class="fas fa-sign-out-alt"></i>
              로그아웃
            </button>
          </div>
        </div>
        
        <router-link v-else to="/login" class="login-btn">
          <i class="fas fa-user"></i>
        </router-link>
      </div>
    </nav>

    <!-- 메인 컨텐츠 -->
    <main class="main-content">
      <div class="content-wrapper">
        <router-view></router-view>
      </div>
    </main>

    <!-- 하단 네비게이션 바 -->
    <nav class="bottom-navbar">
      <div class="nav-wrapper">
        <router-link to="/news" class="nav-item">
          <i class="fas fa-newspaper"></i>
          <span>PI소식</span>
        </router-link>
        
        <router-link to="/write" class="nav-item write-btn">
          <div class="write-btn-circle">
            <i class="fas fa-plus"></i>
          </div>
        </router-link>
        
        <router-link to="/community" class="nav-item">
          <i class="fas fa-users"></i>
          <span>커뮤니티</span>
        </router-link>
      </div>
    </nav>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue'
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth'
import { useRouter } from 'vue-router'
import logoImage from '@/assets/logo.png'

export default {
  name: 'App',
  setup() {
    const auth = getAuth()
    const router = useRouter()
    const currentUser = ref(null)
    const showDropdown = ref(false)
    const defaultAvatar = '/path/to/default-avatar.png'

    // 인증 상태 감시
    let unsubscribe
    onMounted(() => {
      unsubscribe = onAuthStateChanged(auth, (user) => {
        currentUser.value = user
      })
    })

    onUnmounted(() => {
      if (unsubscribe) unsubscribe()
    })

    // 드롭다운 토글
    const toggleDropdown = () => {
      showDropdown.value = !showDropdown.value
    }

    // 드롭다운 외부 클릭시 닫기
    const closeDropdown = (e) => {
      if (!e.target.closest('.user-profile')) {
        showDropdown.value = false
      }
    }

    onMounted(() => {
      document.addEventListener('click', closeDropdown)
    })

    onUnmounted(() => {
      document.removeEventListener('click', closeDropdown)
    })

    // 로그아웃 처리
    const handleLogout = async () => {
      try {
        await signOut(auth)
        router.push('/login')
      } catch (error) {
        alert(error.message)
      }
    }

    return {
      currentUser,
      showDropdown,
      defaultAvatar,
      toggleDropdown,
      handleLogout,
      logoImage
    }
  }
}
</script>

<style>
/* 스크롤바 숨기기 */
body {
  margin: 0;
  padding: 0;
  background-color: #f8f9fa;
  -ms-overflow-style: none;
  scrollbar-width: none;
  font-family: 'Pretendard', -apple-system, BlinkMacSystemFont, system-ui, Roboto, sans-serif;
}

body::-webkit-scrollbar {
  display: none;
}

#app {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #1a1a1a;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

/* 네비게이션 공통 스타일 */
.nav-wrapper {
  max-width: 768px;
  margin: 0 auto;
  width: 100%;
  padding: 0 16px;
  box-sizing: border-box;
}

/* 상단 네비게이션 바 */
.top-navbar {
  background-color: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

.top-navbar .nav-wrapper {
  height: 60px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.logo-link {
  display: flex;
  align-items: center;
  text-decoration: none;
}

.logo-image {
  height: 28px;
  width: auto;
  object-fit: contain;
}

.login-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 24px;
  background-color: #f8f9fa;
  color: #1a1a1a;
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.2s ease;
}

.login-btn:hover {
  background-color: #e9ecef;
}

/* 하단 네비게이션 바 */
.bottom-navbar {
  background-color: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
}

.bottom-navbar .nav-wrapper {
  height: 65px;
  display: flex;
  justify-content: space-around;
  align-items: center;
}

.nav-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-decoration: none;
  color: #1a1a1a;
  font-size: 0.75rem;
  font-weight: 500;
  gap: 4px;
  padding: 8px 12px;
  transition: all 0.2s ease;
}

.nav-item i {
  font-size: 1.25rem;
}

.nav-item:hover {
  color: #228be6;
}

/* 글쓰기 버튼 */
.write-btn-circle {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #228be6 0%, #15aabf 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(34, 139, 230, 0.3);
  transition: all 0.3s ease;
  margin-bottom: 4px;
}

.write-btn-circle i {
  color: white;
  font-size: 1.2rem;
}

.write-btn:hover .write-btn-circle {
  transform: translateY(-4px);
  box-shadow: 0 6px 16px rgba(34, 139, 230, 0.4);
}

/* 메인 컨텐츠 영역 */
.main-content {
  flex: 1;
  margin-top: 60px;
  margin-bottom: 65px;
  background-color: #f8f9fa;
}

.content-wrapper {
  max-width: 768px;
  margin: 0 auto;
  background-color: white;
  min-height: calc(100vh - 125px);
  padding: 20px 16px;
}

/* 사용자 프로필 스타일 */
.user-profile {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 24px;
  background-color: #f8f9fa;
  cursor: pointer;
  transition: all 0.2s ease;
}

.user-profile:hover {
  background-color: #e9ecef;
}

.username {
  font-size: 0.9rem;
  font-weight: 500;
  color: #1a1a1a;
}

.user-profile i {
  font-size: 0.8rem;
  color: #6b7280;
  transition: transform 0.2s ease;
}

.user-profile:hover i {
  transform: translateY(2px);
}

/* 드롭다운 메뉴 스타일 */
.dropdown-menu {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  min-width: 180px;
  padding: 8px;
  z-index: 1000;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  color: #4b5563;
  text-decoration: none;
  font-size: 0.9rem;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.dropdown-item i {
  font-size: 1rem;
  color: #6b7280;
}

.dropdown-item:hover {
  background-color: #f3f4f6;
}

.dropdown-item.logout {
  color: #ef4444;
  border: none;
  background: none;
  width: 100%;
  text-align: left;
  cursor: pointer;
}

.dropdown-item.logout i {
  color: #ef4444;
}

.dropdown-item.logout:hover {
  background-color: #fef2f2;
}
</style>
